import { Arr } from '@deqo/foundation';
import { darken } from 'polished';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { titleCase } from 'title-case';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { Check } from '../../../ui/Check';
import { DropButton } from '../../../ui/DropButton';
import { HelpTip } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { ToolDefinition } from '../tooling/ToolDefinition';
import { ToolManager } from '../tooling/ToolManager';
import { Colors } from './../../../ui';
import { SetupPhaseModel } from './SetupPhaseModel';

const help = {
    collaboration: `
        Enable or disable tools & functions designed 
        for ideating, brainstorming and collaborating.
    `,
    design: `
        Enable/disable the design tools used for 
        building board layouts or diagrams.
    `,
    gestures: `
        Enable/disable gestures people can make to 
        communicate with each other.  None of these 
        tools can change data on the board.
    `,
};

const Push = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
`;

const ToolGridHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .25rem;
    margin: .5rem .75rem;
    border-bottom: solid .0625rem ${Colors.border};

    ${Title} {
        margin: 0;
        color: ${Colors.textLight};
        font-weight: 500;
        font-size: .8125rem;
        text-transform: uppercase;
    }
`;

const ToolGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: .375rem;
    grid-row-gap: .375rem;
    margin: .25rem .75rem;
`;

const ToolGridItem = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    padding: .5rem;
    font-weight: 400;
    user-select: none;
    color: ${Colors.text};

    & > img, & > svg {
        margin-right: 1rem;
        color: ${Colors.primaryText};
    }    

    ${Copy} {
        margin-right: 1rem;
        margin-bottom: 0;
    }
`;

const A = styled.a`
    cursor: pointer;
    user-select: none;
    color: ${Colors.primary};
    &:active { color: ${darken(0.2, Colors.primary)}; }
    &:hover { color: ${darken(0.1, Colors.primary)}; }
`;

const ToolCategory = (id: string, label: string, tools: ToolDefinition[]) => ({ id, label, tools });
type ToolCategory = ReturnType<typeof ToolCategory>;

const toolsTemplate = ['collaboration', 'design', 'gestures'];
const reduceTools = (t: ToolManager) => {
    const selected = t
        .filter(x => !!x.icon)
        .sort((a, b) => a.weight - b.weight);
    const grouped = Arr.groupBy(selected, 'category');
    return toolsTemplate.map(x => ToolCategory(x, titleCase(x), grouped[x]));
};

export const SetupPhaseView: LFC<{ phase: SetupPhaseModel, createNew?: boolean }> = ({ phase, createNew }) => {

    const { tools } = useObserver(phase);

    const toolCats = reduceTools(useResolve(ToolManager));

    const anySelected = useCallback((tc: ToolCategory) => {
        for (const td of tc.tools) {
            if (phase.tools[td.id]) return true;
        }
        return false;
    }, [tools]);

    const onToggleAll = useCallback((value: boolean, tc: ToolCategory) => {
        const toggle = Object.fromEntries(tc.tools.map(x => [x.id, value]));
        phase.update(p => {
            p.tools = { ...p.tools, ...toggle };
        });
    }, [phase, toolCats]);

    return <Flexer flex="column" gap=".25">
        {toolCats.map(tc =>
            <Flexer key={tc.id}>
                <ToolGridHeader>
                    <Title>
                        {tc.label}
                        <HelpTip content={help[tc.id]} />
                    </Title>
                    {anySelected(tc) ? (
                        <A onClick={() => onToggleAll(false, tc)}>Clear All</A>
                    ) : (
                        <A onClick={() => onToggleAll(true, tc)}>Select All</A>
                    )}
                </ToolGridHeader>
                <ToolGrid>
                    {tc.tools.map(x =>
                        <DropButton key={x.id} overflow="0" onClick={() => phase.update(s => s.tools[x.id] = !s.tools[x.id])}>
                            <ToolGridItem>
                                <Icon size="1.75" src={x.icon} />
                                <Copy>{x.label}</Copy>
                                <Push><Check value={!!phase.tools[x.id]} /></Push>
                            </ToolGridItem>
                        </DropButton>
                    )}
                </ToolGrid>
            </Flexer>
        )}
    </Flexer>;
};
